/**
 * Motor de consulta OFFLINE sobre las filas cacheadas de una tabla
 * (ver table-replica-db.ts). Replica —de forma acotada— lo que el server hace
 * en `createStandardPaginatedQuery`: filtra por estado, rango numérico y
 * búsqueda de texto, ordena por recencia y pagina. Función pura y testeable.
 *
 * v1 cubre los filtros comunes (status, numericRanges, search). Filtros
 * específicos de una tabla que no estén acá simplemente no se aplican offline
 * (se ven más filas, nunca menos de lo correcto por status/rango/búsqueda).
 */

/**
 * Alcance de la réplica de una tabla. Las colecciones comparten la tabla
 * `collection_records`: sin separar por `collectionId`, una tabla servida
 * offline mostraba filas de las demás colecciones y el tope de filas era
 * compartido (abrir una colección grande desalojaba a las otras).
 */
export function replicaScopeOf(tableName: string, queryParams: Record<string, unknown>): string {
  const collectionId = queryParams.collectionId;
  return typeof collectionId === "string" && collectionId ? `${tableName}:${collectionId}` : tableName;
}

export interface OfflineQueryParams {
  search?: string;
  status?: string;
  /** Formato del server: "<campo>_<min>_<max>" (min/max o "null"). */
  numericRanges?: string;
  /** Página actual (1-indexed). */
  page: number;
  pageSize: number;
}

export interface OfflineQueryResult<T> {
  page: T[];
  totalCount: number;
}

function parseNumericRange(
  raw?: string,
): { field: string; min?: number; max?: number } | null {
  if (!raw) return null;
  const parts = raw.split("_");
  if (parts.length < 3) return null;
  const field = parts[0];
  const minRaw = parts[1];
  const maxRaw = parts[2];
  if (!field) return null;
  return {
    field,
    min: minRaw && minRaw !== "null" ? Number(minRaw) : undefined,
    max: maxRaw && maxRaw !== "null" ? Number(maxRaw) : undefined,
  };
}

export function queryCachedRows<T extends Record<string, unknown>>(
  rows: T[],
  params: OfflineQueryParams,
): OfflineQueryResult<T> {
  let out = rows;

  // ─── status ───
  if (params.status && params.status !== "all") {
    out = out.filter((r) => String(r.status ?? "") === params.status);
  }

  // ─── rango numérico ───
  const range = parseNumericRange(params.numericRanges);
  if (range) {
    out = out.filter((r) => {
      const v = Number(r[range.field]);
      if (Number.isNaN(v)) return false;
      if (range.min !== undefined && v < range.min) return false;
      if (range.max !== undefined && v > range.max) return false;
      return true;
    });
  }

  // ─── búsqueda (substring sobre campos string, incluidos los de relaciones) ───
  const term = params.search?.trim().toLowerCase();
  if (term) {
    out = out.filter((r) => rowMatchesTerm(r, term));
  }

  // ─── orden: más recientes primero ───
  out = [...out].sort(
    (a, b) => Number(b._creationTime ?? 0) - Number(a._creationTime ?? 0),
  );

  const totalCount = out.length;
  const size = Math.max(1, params.pageSize);
  const start = Math.max(0, (params.page - 1) * size);
  return { page: out.slice(start, start + size), totalCount };
}

/** Busca el término en valores string de la fila y de sus objetos anidados
 *  (p. ej. `client.name`, `seller.name`), un nivel de profundidad. */
function rowMatchesTerm(row: Record<string, unknown>, term: string): boolean {
  for (const value of Object.values(row)) {
    if (typeof value === "string") {
      if (value.toLowerCase().includes(term)) return true;
    } else if (value && typeof value === "object") {
      for (const nested of Object.values(value as Record<string, unknown>)) {
        if (
          typeof nested === "string" &&
          nested.toLowerCase().includes(term)
        ) {
          return true;
        }
      }
    }
  }
  return false;
}
